/**
 * A choice between two to five modes that switch what the page shows:
 * URL / Suchbegriff / Manuell, Deutsch / English. A track with the chosen
 * segment raised; each segment a toggle button (`aria-pressed`).
 *
 * For filters over a list (a Rubrik, a count per option) use `Chips`.
 */

import type { LucideIcon } from 'lucide-react'

import { cn, FOCUS_RING_INSET } from './styles'

export interface SegmentOption<T extends string> {
    value: T
    label: string
    icon?: LucideIcon
}

interface SegmentedControlProps<T extends string> {
    /** The group's accessible name, e.g. "Eingabe". */
    label: string
    options: ReadonlyArray<SegmentOption<T>>
    value: T
    onChange: (value: T) => void
    /** `full` stretches the segments over the available width (side panels). */
    full?: boolean
    size?: 'sm' | 'md'
    disabled?: boolean
    className?: string
}

export default function SegmentedControl<T extends string>({
    label,
    options,
    value,
    onChange,
    full = false,
    size = 'md',
    disabled = false,
    className,
}: SegmentedControlProps<T>) {
    return (
        <div
            role="group"
            aria-label={label}
            className={cn('rounded-full bg-surface-sunken p-1', full ? 'flex' : 'inline-flex', className)}
        >
            {options.map(({ value: option, label: optionLabel, icon: Icon }) => {
                const active = option === value
                return (
                    <button
                        key={option}
                        type="button"
                        onClick={() => onChange(option)}
                        aria-pressed={active}
                        disabled={disabled}
                        className={cn(
                            'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                            size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-1.5 text-sm',
                            full && 'flex-1',
                            active ? 'bg-surface text-primary shadow-xs' : 'text-fg-secondary hover:text-fg',
                            FOCUS_RING_INSET,
                        )}
                    >
                        {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
                        {optionLabel}
                    </button>
                )
            })}
        </div>
    )
}
